import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { 
  Card, 
  Row, 
  Col, 
  Avatar, 
  Typography, 
  Descriptions, 
  Button, 
  Spin,
  Alert,
  Tag
} from 'antd';
import { 
  ArrowLeftOutlined, 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  CalendarOutlined,
  EditOutlined
} from '@ant-design/icons';
import API from '../../API';
import { notifyError } from '../../Util';

const { Title, Text } = Typography;

/**
 * @component
 * @name UserDetailView
 * @description Detailed view of a single user with all their information
 */
const UserDetailView = () => {
  const { userId } = useParams();
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
  // Try to get user by ID
  const payload = await API.get(`users/${userId}`);
  // API.get returns response.data.data; for detail endpoint it's the user object itself
  setUser(payload && payload.data ? payload.data : payload);
    } catch (err) {
      console.error('Error loading user details:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load user details';
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleEdit = () => {
    // Navigate back to users list with edit mode
    history.push('/users', { editUserId: userId });
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U';
  };

  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
          >
            Back
          </Button>
        </div>
        <Alert
          message="Error Loading User"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            type="text"
          >
            Back
          </Button>
        </div>
        <Alert
          message="User Not Found"
          description="The requested user could not be found."
          type="warning"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header with back button and edit button */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={handleBack}
          type="text"
        >
          Back to Users
        </Button>
      
      </div>

      <Row gutter={[24, 24]}>
        {/* User Profile Card */}
        <Col xs={24} lg={12}>
          <Card title="User Profile" bordered={false}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Avatar size={100} style={{ backgroundColor: '#1890ff', fontSize: '36px' }}>
                {getUserInitials(user)}
              </Avatar>
              <Title level={3} style={{ marginTop: '12px', marginBottom: '4px' }}>
                {getUserDisplayName(user)}
              </Title>
              <Text type="secondary" style={{ fontSize: '16px' }}>{user.email}</Text>
            </div>
            
            <Descriptions column={1} bordered size="middle">
              <Descriptions.Item label={<><UserOutlined /> Full Name</>}>
                {getUserDisplayName(user)}
              </Descriptions.Item>
              <Descriptions.Item label={<><MailOutlined /> Email</>}>
                {user.email || 'Not provided'}
              </Descriptions.Item>
              <Descriptions.Item label={<><PhoneOutlined /> Phone</>}>
                {user.phone || 'Not provided'}
              </Descriptions.Item>
              <Descriptions.Item label="Title">
                {user.title || 'Not provided'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={user.status === 'active' ? 'green' : 'red'}>
                  {user.status || 'Unknown'}
                </Tag>
              </Descriptions.Item>
              {user.created_at && (
                <Descriptions.Item label={<><CalendarOutlined /> Member Since</>}>
                  {new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Descriptions.Item>
              )}
              {user.updated_at && (
                <Descriptions.Item label="Last Updated">
                  {new Date(user.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Roles and Permissions Card */}
        <Col xs={24} lg={12}>
          <Card title="Roles & Permissions" bordered={false}>
            <div style={{ marginBottom: '16px' }}>
              <Title level={5}>Assigned Roles</Title>
              {user.roles && user.roles.length > 0 ? (
                <div style={{ marginBottom: '16px' }}>
                  {user.roles.map((role) => (
                    <Tag key={role.id} color="blue" style={{ marginBottom: '8px' }}>
                      {role.name}
                    </Tag>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No roles assigned</Text>
              )}
            </div>

            {user.roles && user.roles.length > 0 && (
              <div>
                <Title level={5}>Permissions</Title>
                <div>
                  {user.roles.flatMap(role => role.permissions || []).length > 0 ? (
                    user.roles.flatMap(role => role.permissions || [])
                      .filter((permission, index, self) => 
                        self.findIndex(p => p.id === permission.id) === index
                      )
                      .map((permission) => (
                        <Tag key={permission.id} style={{ marginBottom: '4px' }}>
                          {permission.name}
                        </Tag>
                      ))
                  ) : (
                    <Text type="secondary">No permissions assigned</Text>
                  )}
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Additional Information Card */}
      {(user.procuring_entities || user.projects || user.councils) && (
        <Row style={{ marginTop: '24px' }}>
          <Col span={24}>
            <Card title="Additional Information" bordered={false}>
              <Row gutter={[16, 16]}>
                {user.procuring_entities && user.procuring_entities.length > 0 && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Procuring Entities</Title>
                    {user.procuring_entities.map((entity) => (
                      <Tag key={entity.id} color="purple" style={{ marginBottom: '4px' }}>
                        {entity.name}
                      </Tag>
                    ))}
                  </Col>
                )}
                
                {user.projects && user.projects.length > 0 && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Projects</Title>
                    {user.projects.map((project) => (
                      <Tag key={project.id} color="orange" style={{ marginBottom: '4px' }}>
                        {project.name}
                      </Tag>
                    ))}
                  </Col>
                )}
                
                {user.councils && user.councils.length > 0 && (
                  <Col xs={24} md={8}>
                    <Title level={5}>Councils</Title>
                    {user.councils.map((council) => (
                      <Tag key={council.id} color="cyan" style={{ marginBottom: '4px' }}>
                        {council.name}
                      </Tag>
                    ))}
                  </Col>
                )}
              </Row>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default UserDetailView;